import { useEffect, useRef, useState } from 'react';
import { messaging } from '@cwos/api-client';
import { useFocusTrap } from '@cwos/ui-admin';

/**
 * GIF picker sheet (doc 01 comms). Searches the existing `/messaging/gifs`
 * endpoint — the ONLY thing that leaves Phibian is the word the user typed; the
 * conversation, channel and people are never sent. When no provider is
 * configured (or it is down, or the org switched GIFs off) the endpoint answers
 * `available: false` with a reason and this shows that honestly rather than
 * spinning. Picking a result hands a fully-formed, allowlist-validated GIF
 * attachment back; the server re-validates before storing it.
 */

type GifResult = messaging.GifSearchResult;

export interface GifPickerProps {
  onPick: (attachment: messaging.GifAttachment) => void;
  onClose: () => void;
}

function toAttachment(r: GifResult): messaging.GifAttachment {
  return {
    kind: 'gif',
    provider: 'tenor',
    providerAssetId: r.providerAssetId,
    previewUrl: r.previewUrl,
    mediaUrl: r.mediaUrl,
    width: r.width,
    height: r.height,
    altText: r.altText,
  };
}

export function GifPicker({ onPick, onClose }: GifPickerProps) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<GifResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    let cancelled = false;
    debounce.current = setTimeout(() => {
      setLoading(true);
      messaging
        .searchGifs(q.trim() || undefined)
        .then((res) => {
          if (cancelled) return;
          if (!res.available) {
            setUnavailable(res.reason ?? 'GIF search is not available.');
            setResults([]);
          } else {
            setUnavailable(null);
            setResults(res.results);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setUnavailable('We couldn’t reach GIF search just now.');
            setResults([]);
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [q]);

  // `autoFocus` moved focus in but never handed it back; the trap does both,
  // and adds the Escape this sheet had no keyboard dismissal for at all.
  const searchRef = useRef<HTMLInputElement>(null);
  const sheetRef = useFocusTrap<HTMLDivElement>(true, {
    initialFocus: searchRef,
    onEscape: onClose,
  });

  return (
    <div ref={sheetRef} className="msg-gif-sheet" role="dialog" aria-label="Choose a GIF">
      <div className="msg-gif-head">
        <input
          ref={searchRef}
          className="msg-input"
          type="search"
          value={q}
          placeholder="Search GIFs…"
          aria-label="Search GIFs"
          onChange={(e) => setQ(e.target.value)}
        />
        <button type="button" className="rn-link" onClick={onClose}>
          Close
        </button>
      </div>

      {loading ? (
        <p className="rn-muted">Searching…</p>
      ) : unavailable ? (
        <p className="rn-muted">{unavailable}</p>
      ) : results.length === 0 ? (
        <p className="rn-muted">No GIFs for “{q}”.</p>
      ) : (
        <div className="msg-gif-grid">
          {results.map((r) => (
            <button
              key={r.providerAssetId}
              type="button"
              className="msg-gif-item"
              onClick={() => onPick(toAttachment(r))}
              aria-label={`Send GIF: ${r.altText}`}
            >
              <img src={r.previewUrl} alt={r.altText} loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
